import { Box, Stack, Typography } from '@mui/joy';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { FormSectionHeader, LabeledValue } from 'core/elements';
import { NumberHelper } from 'core/helpers';
import { useFormContext } from 'core/states';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { WEEKDAYS } from './constants';
import { Day } from './Day';
import { IntervalControls } from './IntervalControls';
import { MonthPicker } from './MonthPicker';
import { useTimeLogIntervalContext } from './useTimeLogInterval';
import { getTimeLogCalendarDays } from './utils';

interface IProps {
  label?: string;
}

export const TimeLogCalendar = ({ label }: IProps) => {
  const [currentDate, setCurrentDate] = useState(() => dayjs());

  const { t } = useTranslation();

  const { errors, watch } = useFormContext();

  const { intervalStart, intervalEnd } = useTimeLogIntervalContext();

  const logsValue = watch('timeLogs');
  const hasError = Boolean(errors?.timeLogs);

  const timeLogCalendarDays = useMemo(
    () => getTimeLogCalendarDays(currentDate, logsValue),
    [currentDate, logsValue]
  );

  const totalHours = useMemo(() => {
    return timeLogCalendarDays
      .filter((day) => day.isCurrentMonth && day.hours > 0)
      .reduce((sum, day) => sum + day.hours, 0);
  }, [timeLogCalendarDays]);

  const navigateMonth = useCallback((direction: number) => {
    setCurrentDate((prev) => prev.add(direction, 'month'));
  }, []);

  const intervalControlsPosition = useMemo(() => {
    if (!intervalEnd) {
      return null;
    }

    const endDayIndex = timeLogCalendarDays.findIndex((day) => intervalEnd.isSame(day.date, 'day'));

    if (endDayIndex === -1) {
      return null;
    }

    const rowIndex = Math.floor(endDayIndex / 7);

    return `calc(${rowIndex + 1} * 75px + 28px + 5px)`;
  }, [intervalEnd, timeLogCalendarDays]);

  return (
    <Stack sx={{ width: '100%', gap: 2 }}>
      {label && <FormSectionHeader title={label} />}

      <Stack
        direction={Direction.ROW}
        sx={{ mb: 2, position: 'relative', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <MonthPicker currentDate={currentDate} setCurrentDate={setCurrentDate} />

        <Stack direction={Direction.ROW} spacing={0.5}>
          <Button
            color={Color.PRIMARY}
            variant={Variant.PLAIN}
            size={Size.SM}
            onClick={() => navigateMonth(-1)}
            icon={<ArrowLeft />}
          />
          <Button
            color={Color.PRIMARY}
            variant={Variant.PLAIN}
            size={Size.SM}
            onClick={() => navigateMonth(1)}
            icon={<ArrowRight />}
          />
        </Stack>
      </Stack>

      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
          }}
        >
          {WEEKDAYS.map((day: string, index: number) => (
            <Typography
              key={`${day}-${index}`}
              level={Level.TITLE_SM}
              sx={{ color: 'neutral.500', paddingBottom: 1 }}
            >
              {day}
            </Typography>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            border: '1px solid',
            borderColor: hasError ? 'danger.300' : 'neutral.outlinedBorder',
            overflow: 'hidden',
          }}
        >
          {timeLogCalendarDays.map((day, index) => (
            <Day key={day.dateKey} day={day} index={index} totalDays={timeLogCalendarDays.length} />
          ))}
        </Box>

        {intervalStart && intervalEnd && intervalControlsPosition && (
          <Box
            sx={{
              position: 'absolute',
              left: '5%',
              right: '5%',
              top: intervalControlsPosition,
              zIndex: 10,
              padding: 2,

              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'neutral.outlinedBorder',
              backgroundColor: 'background.surface',

              /* elevation/9 */
              boxShadow: '0 5px 6px 0 rgba(0, 0, 0, 0.20)',
            }}
          >
            <IntervalControls />
          </Box>
        )}
      </Box>

      <LabeledValue
        label={t('labelTotalTime')}
        value={t('labelTimeValueHours', {
          value: totalHours ? NumberHelper.format({ amount: totalHours }) : DEFAULT_PLACEHOLDER,
        })}
        options={{
          inline: true,
          labelLevel: Level.TITLE_MD,
          valueLevel: Level.TITLE_MD,
          valueSx: { fontWeight: 600 },
        }}
      />
    </Stack>
  );
};
